import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DropDownButton, DropDownButtonItemClickEvent } from '@progress/kendo-react-buttons';
import { UserApi } from '../../../../redux/api/UserApi';
import { ITicketProps } from '../../../../types/Ticket/ITicket';
import { useReduxDispatch, useReduxSelector } from '../../../../redux/hooks';
import { isMobile } from 'react-device-detect';
import { ISetting } from '../../../../types/User/ISetting';
import { Setting } from '../../../../types/Setting/Setting';
import AddActionButton from './AddActionButton';
import Tools, { ITools } from './Tools/Tools';
import * as svg from '@progress/kendo-svg-icons';
import { Align } from '@progress/kendo-react-popup';
import { Sortable, SortableItemUIProps, SortableOnDragOverEvent } from '@progress/kendo-react-sortable';
import { Checkbox } from '@progress/kendo-react-inputs';
import { PartialSpinny } from '../../../Nav/Spinny';
import { SelectedAction, SetSelectedActions } from '../../../../slices/SelectedActionsSlice';
import ToggleView from './Tools/ToggleView';


const ActionToolbar: React.FC<ITicketProps> = (ticket) => {
    const navigate = useNavigate();
    const { data: userProfile } = UserApi.useGetUserProfileQuery();
    const dispatch = useReduxDispatch();
    const { selectedActions, currentPageActionIds } = useReduxSelector(state => state.selectedActionsStore);
    const [rightExpanded, setRightExpanded] = React.useState(false);
    const { data: userState } = UserApi.useGetSettingQuery(Setting.TOOLBAR_STATE);
    const [editMode, setEditMode] = React.useState(false);
    const [showMenu, setShowMenu] = React.useState(false);
    const [userOptions, setUserOptions] = React.useState<ToolbarItem[]>();
    const [updateContactSetting, { isLoading: isSaving }] = UserApi.useSaveSettingMutation();
    const [toolState, setToolState] = React.useState<Array<ITools>>();


    const actionCheckAll = (all: boolean) => {
        console.log('actionCheckAll', all, currentPageActionIds, selectedActions);
        //Check or uncheck all actions
        if (all) {
            const selected = currentPageActionIds.filter(c => selectedActions.find(s => s.actionId === c) === undefined).map(a => { return { actionId: a, ticketId: ticket.ID } as SelectedAction }).concat(selectedActions);
            dispatch(SetSelectedActions([...selected]));
        }
        else {
            //remove the current Page actions
            const whatsLeft = selectedActions?.filter(a => currentPageActionIds.indexOf(a.actionId) === -1);
            dispatch(SetSelectedActions(whatsLeft));
        }
    }

    useEffect(() => {
        if (userState?.Big_String_Value) {
            try {
                var options = JSON.parse(userState.Big_String_Value) as ToolbarItem[];
                setUserOptions(options)
            } catch (e) {
                throw e;
                console.error('Error parsing toolbar state', e);
            }
        }

    }, [userState]);


    const onSave = (state) => {
        //Update the order and visibility of the user's options
        let newTools = [] as ToolbarItem[];
        let saveOptions = userOptions.map((o, i) => {
            let tool = toolState.find(t => t.id === parseInt(o.value));

            if (tool) {
                o.order = toolState.indexOf(tool);
                o.visibility = tool.docked ? "1" : "0";
            }
            //console.warn('saveOptions', o, tool?.order, tool?.docked, toolState.indexOf(tool));

            return o;
        });

        //Add any new tools that are not in the user's options
        toolState.filter(t => t.visible && !userOptions.find(u => u.value === t.id.toString())).map(t => {
            //console.warn('Adding new tool', t)
            saveOptions.push({ Title: t.header, value: t.id.toString(), order: toolState.indexOf(t), visibility: t.docked ? "1" : "0" } as ToolbarItem);
        });

        const saveData = {
            Setting_Id: Setting.TOOLBAR_STATE,
            Big_String_Value: saveOptions ? JSON.stringify(saveOptions) : null
        } as ISetting

        updateContactSetting(saveData).then(() => {

            setEditMode(false);
        });
    }

    const onCancel = () => {
        //setData(defaultTileArray);
        setEditMode(false);
    }

    const resetLayout = () => {
        if (!window.confirm('Are you sure you want to reset the sidebar layout?')) return;

        //console.log('resetting layout', defaultState, data);
        //setPositions(() => defaultPosition);
        //console.log('resetting layout 1', defaultState, data);
        //saveChanges
        //onSave(defaultPosition);
    }

    let tools = Tools(userProfile);

    //Load the tools and the user's order of the tools into state
    useEffect(() => {
        if (tools?.length && !toolState && userState) {
            if (userState?.Big_String_Value) {
                try {
                    var _userOptions = JSON.parse(userState.Big_String_Value.replaceAll('\r\n', '')) as ToolbarItem[];
                    console.warn('userOptions', _userOptions);
                    //Remove duplicates based on the Value field
                    _userOptions = _userOptions.filter((o, i, a) => a.findIndex(t => t.value === o.value) === i);
                    console.warn('filtered userOptions', _userOptions);

                    //reorder the tools based on the user's order
                    _userOptions.map(o => {
                        var t = tools.find(t => t.id === parseInt(o.value));
                        if (t) {
                            t.order = o.order;
                            t.docked = parseInt(o.visibility) === 1 && !isMobile;
                            if ((t.id === 12 || t.id === 8) && isMobile) t.docked = true; //Send email is always docked in mobile
                        }
                    })
                    setUserOptions(_userOptions);
                    //console.warn('userOptions', tools, userOptions, userState.Big_String_Value)
                } catch (e) {
                    //throw e;
                    console.error('Error parsing toolbar state', e);
                }
            }

            //Sort and put in state
            tools.sort((a, b) => a.order - b.order);
            setToolState(tools);
        }
    }, [tools, userState]);

    //Not for self service
    //if (!tiles?.length) return null;

    const menuItemClicked = (e: DropDownButtonItemClickEvent) => {
        console.log('menuItemClicked', e)
        if (e.item.id === -1) {
            setEditMode(true);
            return;
        }
        navigate(e.item.route);
    }
    const getBaseItemStyle = (isActive: boolean) => ({
        height: 70,
        lineHeight: '68px',
        fontSize: '16px',
        textAlign: 'center',
        outline: 'none',
        border: '1px dashed',
        cursor: 'move',
        display: 'inline-block',
        paddingLeft: 6,
        paddingRight: 6,
        marginRight: 6,
        borderColor: '#000',
        width: 'aUTO',
    });

    const SortableItemUI = (props: SortableItemUIProps) => {
        const { isDisabled, isActive, style, attributes, dataItem, forwardRef } = props;
        const classNames = ['col-xs-6 col-sm-3'];

        if (isDisabled) {
            classNames.push('k-disabled');
        }

        return (
            <div
                ref={forwardRef}
                {...attributes}
                style={{
                    ...getBaseItemStyle(isActive),
                    ...style
                }}
                className={classNames.join(' ')}
            >
                <div className='sortable-item' title={dataItem.Title}>
                    <Checkbox
                        checked={dataItem.docked}
                        onChange={(e) => {
                            dataItem.docked = e.value;
                            setToolState([...toolState]);
                        }} />
                    {(dataItem as ITools).body({ ...ticket, Is_Loading: true })}</div>
            </div>
        );
    };

    const onDragOver = (event: SortableOnDragOverEvent) => {
        setToolState(event.newState as Array<ITools>);
    };

    // const menuItems2 = toolState && [...toolState?.filter(t => !t.docked && t.visible), { id: -1, header: 'Configure toolbar items', icon: 'gears' } as ITools].map(t => <div className='d-flex' key={t.id}>
    //     {t.icon ? <Icon name={t.icon} size='large' className=' mr-2' /> : <img src={t.img} alt={t.header} className='toolbar-menu-item mr-2' />}{t.header}</div>);

    const menuItems = toolState && [...toolState?.filter(t => !t.docked && t.visible), { id: -1, header: 'Configure toolbar items...', icon: 'gears' } as ITools].map(t => {
        return { ...t, text: t.header, imageUrl: t.img }
    });

    if (!toolState) return null;

    const allAllActionsSelected = currentPageActionIds?.filter(a => selectedActions?.findIndex(s => s.actionId === a) === -1).length === 0;
    //Selected action mode
    if (selectedActions !== null || selectedActions?.filter(s => s.ticketId === ticket.ID).length > 0)
        return (
            <div className="action-toolbar">

                <div><Button className='check-all-button' onClick={(e) => {
                    //e.preventDefault();
                    // e.stopPropagation();
                    actionCheckAll(!allAllActionsSelected)
                }}><div><Checkbox className='d-inline-block' checked={allAllActionsSelected} /></div><div>All</div></Button></div>
                <div className='ms-3 me-3'><strong>{selectedActions.filter(s => s.ticketId === ticket.ID).length} Selected</strong></div>
                {toolState.filter(t => t.id === 8 || t.id === 4 || t.id === 5).map((t, i) => <div key={i}>{t.body(ticket)}</div>)}
                <Button fillMode='flat' title='Cancel select' svgIcon={svg.cancelIcon} onClick={(e) =>
                    dispatch(SetSelectedActions(null))
                } >{!isMobile && 'Cancel'}</Button>
            </div >)


    return (
        <>
            <div className="action-toolbar">
                {isSaving && <PartialSpinny />}
                <AddActionButton />
                {!userProfile?.Features.External_Interface &&
                    (editMode ? <>
                        <Sortable idField={'id'} data={toolState.filter(t => t.visible)} itemUI={SortableItemUI} onDragOver={onDragOver}>

                        </Sortable>
                        <Button svgIcon={svg.saveIcon} className='m-2' fillMode='solid' title='Save' onClick={onSave}></Button>
                        <Button svgIcon={svg.cancelIcon} className='m-2' fillMode='solid' title='Cancel' onClick={onCancel}></Button>
                    </>
                        : toolState.filter(t => t.docked && t.visible).map((t, i) => <div key={i}>{t.body(ticket)}</div>))
                }
                {/* toolState:{toolState?.filter(t => t.visible).length} */}
                {!userProfile?.Features.External_Interface && !editMode && !isMobile &&
                    <DropDownButton size='large' title='More options...' fillMode='flat' onItemClick={menuItemClicked} items={menuItems} svgIcon={svg.moreHorizontalIcon} popupSettings={{ animate: false, anchorAlign: { horizontal: 'left', vertical: 'bottom' }, popupAlign: { horizontal: 'left' } as Align }}>
                    </DropDownButton>}
                {isMobile && <div className='action-toggle-view'>
                    <ToggleView {...ticket} />
                </div>}
            </div >

        </>
    );
};


type ToolbarItem = {
    Name: string
    Title: string
    value: string
    image: string
    onClick: string
    visibility: string
    feature_Enum: number
    postBack: boolean
    order: number
    showContext: boolean
    asset_Count: number
}


export default ActionToolbar;